module Client.AdminEditUsers

open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open Fable.Core.JsInterop
open Fetch
open Fable
open Shared
open Utils
open AdminShared
open Fable.Import

type Msg =
    | FetchError of exn
    | DeleteError of exn
    | PageClicked of User * ClickAction
    | AddNewUser
    | SaveUser
    | EditPassword
    | SaveNewPassword
    | EditEmail
    | StopEditingEmail
    | SaveNewEmail
    | LoadedUserList of Domain.User list
    | ConfirmDelete
    | ConfirmedDeletion
    | CancelDeletion
    | UserDeleted
    | UserCreationResponse of Validated
    | UserDeletionResponse of Validated
    | UserEmailResponse of Validated
    | UserAdded
    | PasswordUpdated
    | EmailUpdated
    | ReturnToUserList
    | UsernameChanged of string
    | EmailChanged of string

type EditUserState =
    {
        User: Domain.User
        NewPassword: string
        NewEmail: string
        EditingEmail: bool
        Username: string
        IsNew: bool
        SaveErrors: string list
        DeleteError: string
        EmailError: string
    }

type AdminUserAction =
    | Listing of LoadState<unit, Domain.User list>
    | Editing of LoadState<Domain.User, EditUserState> 

type Model =
    {
        User: JWT
        Action: AdminUserAction
        ShowConfirmDelete: bool
    }

let getAllUsersCmd (jwt: JWT) =
    let url = Urls.Users (TenantRef.get jwt.Account.Tenant)
    FetchRequest.getWithToken url jwt.Token LoadedUserList FetchError

let postSaveUserCmd (jwt: JWT) username email = 
    let tenant = TenantRef.get jwt.Account.Tenant
    let user = { Id = 0; Email = email; EmailHash = ""; Username = username; Tenant = TenantRef tenant }
    let url = Urls.Users tenant
    FetchRequest.postWithToken url user jwt.Token UserCreationResponse FetchError

let postSaveEmailCmd (jwt: JWT) username (email: string) = 
    let url = Urls.UserEmail (TenantRef.get jwt.Account.Tenant) username
    FetchRequest.postWithToken url email jwt.Token UserEmailResponse FetchError

let deleteUserCmd (jwt: JWT) username = 
    let url = Urls.UserByName (TenantRef.get jwt.Account.Tenant) username
    FetchRequest.deleteWithToken url username jwt.Token UserDeletionResponse DeleteError

let init user = 
    {
        User = user
        ShowConfirmDelete = false
        Action = Listing (Loading ())
    }, getAllUsersCmd user

let private editing model modifier (cmd: Model -> EditUserState -> Cmd<Msg>) success =
     match model.Action with
        | Editing (Loaded pageState) ->  
           modifier model pageState, cmd model pageState, (success pageState)
        | _ -> failwith "Invalid action"

let private editingAction model modifier cmd success =
     editing model (fun m pageState -> { m with Action = Editing (Loaded(modifier pageState)) }) cmd success

let private editingActionU model modifier cmd = editingAction model modifier cmd (fun _ -> Unchanged)

let dispatchSuccess result msg _ _ =
    match result with 
    | Success -> Cmd.ofMsg msg 
    | _ -> []

let update (msg:Msg) model : Model*Cmd<Msg>* SuccessMessageChange =    
    match msg with
    | LoadedUserList users ->
        { model with Action = Listing (Loaded users) }, [], Unchanged 
    | ReturnToUserList ->
        { model with Action = Listing (Loading()); ShowConfirmDelete = false },getAllUsersCmd (model.User),Unchanged
     | AddNewUser ->
        match model.Action with
        | Listing (Loaded _) ->  
            let newUser =
                { 
                    Id = 0
                    Username = ""
                    Tenant = model.User.Account.Tenant
                    Email = ""
                    EmailHash = ""
                }
            { model with Action = Editing (Loaded { User = newUser; NewPassword = ""; NewEmail = ""; Username = ""; EditingEmail = false; IsNew = true; EmailError = ""; DeleteError = ""; SaveErrors = [] }) }, [], Unchanged
        | _ -> failwith "Invalid action"
    | SaveUser -> 
        editingActionU model id (fun m ps -> postSaveUserCmd m.User ps.Username ps.NewEmail)
    | SaveNewEmail -> 
        editingActionU model id (fun m ps -> postSaveEmailCmd m.User ps.User.Username ps.NewEmail)
    | UserCreationResponse result -> 
        editingActionU model (fun m -> { m with SaveErrors = match result with Success -> [] | Failure msgs -> msgs }) (dispatchSuccess result UserAdded)
    | UserDeletionResponse result -> 
        editingActionU model (fun m -> { m with DeleteError = match result with Success -> "" | Failure msgs -> msgs.[0] }) (dispatchSuccess result UserDeleted)
    | UserEmailResponse result -> 
        editingActionU model (fun m -> { m with EmailError = match result with Success -> "" | Failure msgs -> msgs.[0] }) (dispatchSuccess result EmailUpdated)
    | EmailChanged email -> editingActionU model (fun m -> { m with NewEmail = email }) (fun _ _ -> [])
    | UsernameChanged username -> editingActionU model (fun m -> { m with Username = username }) (fun _ _ -> [])
    | ConfirmDelete -> editingActionU { model with ShowConfirmDelete = true } (fun m -> { m with DeleteError = "" }) (fun _ _ -> [])
    | CancelDeletion -> { model with ShowConfirmDelete = false }, [], Unchanged
    | EditEmail -> editingActionU model (fun m -> { m with EmailError = ""; EditingEmail = true; NewEmail = m.User.Email }) (fun _ _ -> [])
    | StopEditingEmail -> editingActionU model (fun state -> { state with EmailError = ""; EditingEmail = false }) (fun _ _ -> [])
    | ConfirmedDeletion -> 
        editingActionU model id (fun _ x -> deleteUserCmd model.User x.User.Username)
    | UserAdded ->
        editingAction model id (fun _ _ -> Cmd.ofMsg ReturnToUserList) (fun x -> Show (sprintf "The user '%s' was added." x.Username))
    | EmailUpdated ->
        editingAction model (fun ps -> { ps with EditingEmail = false; User = { ps.User with Email = ps.NewEmail } }) (fun _ _ -> []) (fun _ -> Show ("The email address was updated."))
    | UserDeleted ->
        editingAction model id (fun _ _ -> Cmd.ofMsg ReturnToUserList) (fun x -> Show (sprintf "The user '%s' was deleted." x.User.Username))
    | PageClicked (user, action) ->
        match action with
        | Edit ->
            { model with Action = Editing (Loaded { User = user; NewPassword = ""; NewEmail = ""; Username = ""; EditingEmail = false; IsNew = false; EmailError = ""; DeleteError = ""; SaveErrors = [] }) }, [], Unchanged
        | Delete ->
            { model with Action = Editing (Loaded { User = user; NewPassword = ""; NewEmail = ""; Username = ""; EditingEmail = false; IsNew = false; EmailError = ""; DeleteError = ""; SaveErrors = [] }); ShowConfirmDelete = true}, [], Unchanged
    | FetchError exn ->
        model,[],Unchanged
    | DeleteError exn ->
        editingActionU model (fun m -> { m with DeleteError = exn.Message; }) (fun _ _ -> [])


let isEmpty = System.String.IsNullOrWhiteSpace
let notEmpty = System.String.IsNullOrWhiteSpace >> not

let view model dispatch =
    match model.Action with
    | Listing loadstate ->
        match loadstate with
        | Loading () -> 
            [
                yield div [ ClassName "valign" ] [
                    yield h2 [ Style [ MarginRight "10px" ] ] [ str "Users" ] 
                    yield button [ 
                        ClassName "btn animated-background" 
                        HTMLAttr.Type "button"
                    ] [ str "Add new user" ]
                ]
                yield div [] [ str  "There are ? maintained users" ]
                yield table [ ClassName "table animated-background" ] [
                    thead [] [
                        tr [ ClassName "mask" ] [
                            th [ClassName "mask"] [ str "Username" ]
                            th [ClassName "mask"] [ str "Email" ]
                            th [ClassName "mask"] []
                        ]
                    ]
                    tbody [] [
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                                
                        ]
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                              
                        ]
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                                    
                        ]
                    ]
                ]
            ]
        | Loaded users ->
            [
                yield div [ ClassName "valign" ] [
                    yield h2 [ Style [ MarginRight "10px" ] ] [ str "Users" ] 
                    yield button [ 
                        ClassName "btn btn-default" 
                        HTMLAttr.Type "button"
                        OnClick (fun _ -> dispatch AddNewUser)
                    ] [ str "Add new user" ]
                ]
                match users.Length with
                | 0 -> 
                    yield p [ ClassName "bg-info" ] [ str "You have not yet added any users. This is most unexpected." ]
                | n ->
                    yield div [] [ str <| sprintf "There %s %d maintained use%s." (be n) n (plural "r" n) ]
                    yield table [ ClassName "table" ] [
                        thead [] [
                            tr [] [
                                th [] [ str "Username" ]
                                th [] [ str "Email" ]
                                th [] []
                            ]
                        ]
                        tbody [] [
                            yield! users |> List.map (fun user -> 
                                tr [] [ 
                                    td [] [ str user.Username ]
                                    td [] [ str user.Email ]
                                    td [] [ 
                                        a [ jsVoid; OnClick (fun _ -> dispatch (PageClicked(user, Edit))) ] [ str "Edit" ]
                                        a [ jsVoid; OnClick (fun _ -> dispatch (PageClicked(user, Delete))) ] [ str "Delete" ]
                                    ]                                                                    
                                ])
                        ]
                    ]
                    
            ]
    | Editing loadstate -> 
        [     
            match loadstate with
            | Loaded state ->
                yield form [] [
                    formGroup [
                        yield label [ HtmlFor "username" ] [ str "Username" ]
                        if state.IsNew then
                            yield textInput "username" "Enter the username." UsernameChanged dispatch state.Username (Some 200.)
                        else
                            yield p [ ClassName "form-control-static" ] [ str state.User.Username ]
                    ]
                    formGroup [
                        label [ HtmlFor "email" ] [ str "Email" ]
                        div [ ClassName "input-group" ] [
                            if state.IsNew || state.EditingEmail then
                                yield textInput "email" "Enter the email address." EmailChanged dispatch state.NewEmail (Some 500.)
                                if not state.IsNew then
                                    if notEmpty state.NewEmail then yield a [ ClassName "input-group-addon"; jsVoid; OnClick (fun _ -> dispatch SaveNewEmail) ] [ str "save" ]
                                    yield a [ ClassName "input-group-addon"; jsVoid; OnClick (fun _ -> dispatch StopEditingEmail) ] [ str "cancel" ]               
                            else
                                yield p [ ClassName "form-control-static" ] [ str state.User.Email ]
                                yield a [ ClassName "input-group-addon"; jsVoid; OnClick (fun _ -> dispatch EditEmail) ] [ str "change" ]
                        ]
                    ]    
                ]
                if notEmpty state.EmailError then
                    yield p [ ClassName "alert alert-danger" ] [ str state.EmailError ]
                if state.SaveErrors.Length > 0 then
                    yield p [ ClassName "alert alert-danger" ] (state.SaveErrors |> List.map (fun err -> div [] [ str err ]))

                yield footer [] [
                    yield button [ 
                        ClassName "btn btn-primary"
                        HTMLAttr.Type "button"
                        OnClick (fun _ -> dispatch ReturnToUserList)
                    ] [ str "Go Back" ]
                    if state.IsNew then
                        yield button [ 
                            ClassName "btn btn-success"
                            HTMLAttr.Type "button"
                            OnClick (fun _ -> dispatch SaveUser)
                            Disabled (isEmpty state.NewEmail || isEmpty state.Username)
                        ] [ str "Save the User" ]           
                    else
                        yield button [ 
                            ClassName "btn btn-danger"
                            HTMLAttr.Type "button"
                            OnClick (fun _ -> dispatch ConfirmDelete)
                        ] [ str "Delete this user" ]
                ]
                match model.ShowConfirmDelete with
                | false -> ()
                | true -> 
                    yield! modal "Confirm Deletion" dispatch CancelDeletion [
                        yield p [] [ str "Are you sure you wish to delete this user?" ]
                        if state.DeleteError |> notEmpty then
                            yield p [ ClassName "alert alert-danger" ] [ str state.DeleteError ]
                    ] [
                        button [ ClassName "btn btn-danger"; HTMLAttr.Type "button";OnClick (fun _ -> dispatch ConfirmedDeletion) ] [ str "Confirm Deletion" ]
                        button [ ClassName "btn btn-default"; HTMLAttr.Type "button";OnClick (fun _ -> dispatch CancelDeletion); Keyboard.onEscape CancelDeletion dispatch] [ str "Cancel" ]
                    ]
            | Loading _ -> yield str "Loading..."
        ]